// <!-- GRACE:MODULE_START name="ui_asr" -->
// <!-- GRACE:DEPENDENCY target="ui_types" -->
import React, { useState, useRef } from 'react';
import { PlayIcon, PauseIcon, SpeakerWaveIcon } from '@heroicons/react/24/solid';

interface AudioPlayerProps {
  audioUrl: string | null;
  audioBlob: Blob | null;
}

// <!-- GRACE:CONTRACT function="AudioPlayer" -->
const AudioPlayer: React.FC<AudioPlayerProps> = ({ audioUrl, audioBlob }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);

  const handlePlayPause = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleDownload = () => {
    if (audioBlob) {
      const url = URL.createObjectURL(audioBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `medical-dictation-${new Date().toISOString().slice(0, 19)}.webm`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  if (!audioUrl || !audioBlob) {
    return null;
  }

  return (
    <div className="bg-gray-50 rounded-lg p-4 mt-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-2">
          <SpeakerWaveIcon className="w-5 h-5 text-medical-primary" />
          <span className="text-sm font-medium text-medical-text">Записанное аудио</span>
        </div>
        <button
          onClick={handleDownload}
          className="text-xs text-medical-primary hover:text-medical-secondary underline"
        >
          Скачать
        </button>
      </div>

      <div className="flex items-center space-x-3">
        <button
          onClick={handlePlayPause}
          className="flex items-center justify-center w-10 h-10 bg-medical-primary hover:bg-blue-700 text-white rounded-full transition-colors duration-200"
        >
          {isPlaying ? (
            <PauseIcon className="w-5 h-5" />
          ) : (
            <PlayIcon className="w-5 h-5 ml-0.5" />
          )}
        </button>

        <div className="flex-1">
          <div className="flex justify-between text-xs text-medical-muted mb-1">
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-medical-primary h-2 rounded-full transition-all duration-200"
              style={{
                width: duration > 0 ? `${(currentTime / duration) * 100}%` : '0%'
              }}
            />
          </div>
        </div>
      </div>

      <audio
        ref={audioRef}
        src={audioUrl}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
        preload="metadata"
      />
    </div>
  );
};

export default AudioPlayer;
// <!-- GRACE:MODULE_END name="ui_asr" -->
